import React, { useMemo, MouseEventHandler, ChangeEvent } from 'react';
import { Moment } from 'moment';
import Box from '@mui/material/Box';
import { SelectChangeEvent } from '@mui/material/Select';
import { alarmAction } from '@core/interfaces/alarm-action';
import InputComponent from '@ui/components/Input/Input.component';
import SelectComponent from '@ui/components/Select/Select.component';
import SwitchComponent from './Switch.component';
import { BindedHandler } from '@core/interfaces/binded-handler';
import TimeInputComponent from '@ui/components/Time-input/Time-input.component';
import { lang } from '@lang/interfaces/lang';

interface IProps {
    lang: lang;
    name: string;
    alarmState: boolean;
    alarmAction: number;
    alarmActions: alarmAction[];
    wakeUpTime: Moment;
    wakeUpTimeError: boolean;
    wakeUpActiveTime: number;
    wakeUpActiveTimeError: boolean;
    timezone?: number;
    onAlarmStateChange: MouseEventHandler<HTMLButtonElement>;
    onWakeUpChange: BindedHandler<Moment>;
    onWakeUpError: BindedHandler<unknown>;
    onWakeUpActiveTimeChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onAlarmActionChange: BindedHandler<SelectChangeEvent<number>>;
    onInputClick: () => void;
}

const Component: React.FC<IProps> = ({
    lang,
    name,
    alarmState,
    alarmAction,
    alarmActions,
    wakeUpTime,
    wakeUpTimeError,
    wakeUpActiveTime,
    wakeUpActiveTimeError,
    onAlarmStateChange,
    onWakeUpChange,
    onWakeUpError,
    onWakeUpActiveTimeChange,
    onAlarmActionChange,
    onInputClick,
}: IProps) => {
    const actionsList = useMemo(
        () => alarmActions.map((label, value) => ({ label, value })),
        [alarmActions],
    );

    return (
        <Box className="device-settings__alarm-item" component="li">
            <SwitchComponent
                alarmState={alarmState}
                name={name}
                onAlarmStateChange={onAlarmStateChange}
            />
            <TimeInputComponent
                label="wakeUpTime"
                tooltip="wakeUpTimeTooltip"
                lang={lang}
                disabled={!alarmState}
                error={wakeUpTimeError}
                value={wakeUpTime}
                onChange={onWakeUpChange.bind(this, name)}
                onError={onWakeUpError.bind(this, name)}
                onInputClick={onInputClick}
            />
            <InputComponent
                label="periodicActiveTime"
                name="active-time"
                type="number"
                description="periodicActiveTimeTooltip"
                helperText="periodicActiveTimeHelper"
                errorMessage="periodicActiveTimeError"
                lang={lang}
                disabled={!alarmState}
                value={wakeUpActiveTime}
                numberProps={{
                    min: 3,
                    max: 15,
                }}
                error={wakeUpActiveTimeError}
                onChange={onWakeUpActiveTimeChange.bind(this, name)}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            <SelectComponent
                label="action"
                name="alarm-action"
                lang={lang}
                disabled={!alarmState}
                value={alarmAction}
                values={actionsList}
                onChange={onAlarmActionChange.bind(this, name)}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
        </Box>
    );
};

const AlarmItemComponent = React.memo(Component);

export default AlarmItemComponent;
